<script>
  // @ts-check
  import { v4 as uuid } from "@lukeed/uuid";
  import Image from "../../atoms/Image.svelte";
  import Link from "../../atoms/Link.svelte";
  import Tags from "../../atoms/Tags.svelte";
  import { getTheme } from "../../utilities/themes";
  import { getContext } from "svelte";
  import { VALID_FEATURED_LAYOUTS } from "../CardLayoutMixed.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /** The index of the card
   * @type {number} */
  export let index = undefined;

  /**
   * A string of space-separated aspect ratio Tailwind classes.
   * @type {string}
   * @default "aspect-none"
   */
  export let imageAspectRatioClasses = "aspect-none";

  /**

  /** The title of the card
   * @type {string} */
  export let title = undefined;

  /**
   * The URL of the card.
   * @property {string}
   */
  export let cardUrl = undefined;

  /** The position of the image in the card
   * @type { "top" | "left" | "right" } */
  export let imagePosition = "top";

  /**
   * The tag in the card
   * @type {import("../../atoms/Tags.svelte").Tag[]}
   */
  export let tags = null;

  /**
   * The description in the card
   * @type {string}
   */
  export let description = null;

  /**
   * The call to action in the card
   * @type {import('../../types/shared.js').CTA} CTA
   */
  export let cta = undefined;

  /**
   * The featured image in the card
   * @type {import('../../types/shared.js').Image} Image
   */
  export let featuredImage = undefined;

  /**
   * The theme in the card
   * @type {import('../../utilities/themes.js').ThemeName} ThemeName
   */
  export let theme;

  /**
   * The type in the card
   * @type {boolean}
   */
  export let isFeatured = null;

  /**
   * Determines if the card has large text
   * @type {boolean}
   */
  export let hasLargeText = false;

  // Layout-specific Overrides
  const mixedLayoutContext = getContext("CardLayoutMixed");
  let localImagePosition = imagePosition;

  if (isFeatured && mixedLayoutContext?.featuredLayout === VALID_FEATURED_LAYOUTS.DoubleFeatured) {
    imageAspectRatioClasses = "aspect-1/1";
  } else if (
    isFeatured &&
    mixedLayoutContext?.featuredLayout === VALID_FEATURED_LAYOUTS.SingleFullWidthFeature
  ) {
    localImagePosition = imagePosition === "top" ? "left" : imagePosition;
  } else if (
    isFeatured &&
    mixedLayoutContext?.featuredLayout === VALID_FEATURED_LAYOUTS.SingleTwoThirdsFeature &&
    index === 0
  ) {
    imageAspectRatioClasses = "aspect-3/2";
  }

  let ctaId = cta?.id ?? `cta_${uuid()}`;

  let isCardFocused = false;
  let isLinkHovered = false;

  let currentTheme = theme ? getTheme(theme) : getTheme("white");

  let isThemed = theme !== undefined;
  let hasFeatureImage = featuredImage && featuredImage.src !== undefined;

  let containerVariantClass =
    isFeatured && localImagePosition !== "top"
      ? "flex-col lg:flex-row lg:gap-[4.5rem]"
      : "flex-col";
  let containerThemedClass =
    isThemed && currentTheme?.backgroundColor && hasFeatureImage ? "p-3 lg:p-4" : "";

  let containerNoImageClass = !hasFeatureImage ? "p-5 md:p-4 lg:p-6 xl:p-8" : "";

  let containerInfosNoImageClass = !hasFeatureImage
    ? "flex flex-col min-h-[20.625rem] md:min-h-[18.5rem] lg:min-h-[21.5rem] xl:min-h-[30.5rem]"
    : "";

  let containerInfoClass =
    (localImagePosition === "top" || !isFeatured) && hasFeatureImage
      ? "pt-8 md:pt-7 lg:pt-7.5 xl:pt-8"
      : "pt-8 md:pt-7 lg:pt-0";
  let imageClass = isFeatured && localImagePosition === "right" ? "lg:order-last" : "";
  let titleClass = hasLargeText ? "heading-md" : "heading-xs";
  let textClass = !hasFeatureImage ? "grow" : "";
  let linkClass = !hasFeatureImage ? "" : "mt-6 md:mt-7 lg:mt-7.5 xl:mt-8";
</script>

{#if cardUrl && title}
  <div
    {id}
    class="relative flex {containerVariantClass} {currentTheme?.textColor} {currentTheme?.backgroundColor} {containerThemedClass} {containerNoImageClass} {isCardFocused
      ? 'outline-dashed outline-2 outline-offset-2'
      : ''}"
  >
    {#if hasFeatureImage}
      <Image
        {...featuredImage}
        aspectRatioClasses={imageAspectRatioClasses}
        containerClass="w-full h-auto {imageClass} overflow-hidden"
        elementClass="transition {isLinkHovered ? 'scale-[103%]' : ''}"
      />
    {/if}
    <div class="{containerInfosNoImageClass} {containerInfoClass}">
      {#if tags}
        <Tags
          {tags}
          class="label gap-x-5 gap-y-2 pb-3 lg:pb-4 {currentTheme?.translucentTextColor ||
            'text-black-60'}"
          bulletClass="bullet"
        />
      {/if}
      <div class={titleClass}>
        <a
          id={ctaId}
          href={cardUrl}
          target={cta?.url && cta?.isExternal ? "_blank" : null}
          rel={cta?.url && cta?.isExternal ? "noopener noreferrer" : null}
          aria-describedby={ctaId}
          class="no-underline outline-none after:absolute after:bottom-0 after:left-0 after:right-0 after:top-0 after:z-[--stack-level-card] focus-visible:outline-none"
          data-sveltekit-reload
          on:focusin={() => (isCardFocused = true)}
          on:focusout={() => (isCardFocused = false)}
          on:mouseenter={() => (isLinkHovered = true)}
          on:mouseleave={() => (isLinkHovered = false)}
        >
          {title}
        </a>
      </div>
      {#if description}
        <p class="body-sm pt-3 lg:pt-4 {textClass}">{description}</p>
      {/if}
      {#if cta}
        <Link
          id={ctaId}
          isExternal={cta.isExternal}
          ariaHidden={true}
          class={linkClass}
          label={cta.text}
          variant="small"
          {theme}
          {isLinkHovered}
        />
      {/if}
    </div>
  </div>
{/if}
